// =============================================================================
// PTEL - Admin Service
// =============================================================================
// Gestión de usuarios: listado y cambio de rol
// Solo accesible por administradores (protegido por Firestore rules)
// =============================================================================

import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  orderBy,
  query,
} from 'firebase/firestore';
import { auth, db, isConfigured, COLLECTIONS } from '../../config/firebase';
import type { FirebaseUser, UserRole } from '../../types/firebase.types';

// -----------------------------------------------------------------------------
// List Users
// -----------------------------------------------------------------------------

/**
 * PANADM-b1: fallo al leer la lista de usuarios. Lleva un `code` para que el
 * panel lo diga como fallo («Reintentar»), nunca como lista vacía.
 */
export class AdminListError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'AdminListError';
    this.code = code;
  }
}

/** Fecha de Firestore (Timestamp) a `Date`; `null` se conserva; ausente → `undefined`. */
function toDateOrNull(value: unknown): Date | null | undefined {
  if (value === null) return null;
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function codeOf(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code : 'unknown';
}

/**
 * Lista las fichas de `users`. PANADM-b1: si no puede leerlas, **lanza**
 * `AdminListError` (antes devolvía `[]` en silencio y el panel decía «0 usuarios»).
 * Sin Firebase configurado no hay nada que listar y devuelve `[]`.
 */
export async function listUsers(): Promise<FirebaseUser[]> {
  if (!isConfigured || !db) return [];

  // Guard 1: sin sesión activa no hay forma de ser admin
  const currentUser = auth?.currentUser;
  if (!currentUser) {
    throw new AdminListError('sin-sesion', 'No hay sesión iniciada.');
  }

  // Guard 2: verificar el rol propio en Firestore antes de listar (ventana de
  // carrera entre onAuthStateChanged y la carga de la ficha).
  let selfRole: unknown;
  try {
    const selfDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
    selfRole = selfDoc.exists() ? selfDoc.data()?.role : undefined;
  } catch (error) {
    throw new AdminListError(codeOf(error), 'No se ha podido leer tu propia ficha.');
  }
  if (selfRole !== 'admin') {
    throw new AdminListError('no-admin', 'Tu ficha no tiene el rol de administración.');
  }

  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('displayName', 'asc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((d) => {
      const data = d.data();
      const user: FirebaseUser = {
        uid: d.id,
        email: data.email ?? '',
        displayName: data.displayName ?? '',
        role: (data.role as UserRole) ?? 'viewer',
        createdAt: typeof data.createdAt?.toDate === 'function' ? data.createdAt.toDate() : new Date(),
        lastLogin: typeof data.lastLogin?.toDate === 'function' ? data.lastLogin.toDate() : new Date(),
        // PANADM-b1 DOP-0 (a): la ficha no es fuente fiable de la verificación
        // (el alta no la escribe desde SEC0); el panel no la enseña hasta E-CUENTAS.
        emailVerified: data.emailVerified ?? false,
      };
      // PANADM-b1: la caducidad y la concesión reales de la ficha.
      const expiresAt = toDateOrNull(data.roleExpiresAt);
      if (expiresAt !== undefined) user.roleExpiresAt = expiresAt;
      const grantedAt = toDateOrNull(data.roleGrantedAt);
      if (grantedAt) user.roleGrantedAt = grantedAt;
      if (typeof data.roleGrantedBy === 'string') user.roleGrantedBy = data.roleGrantedBy;
      return user;
    });
  } catch (error) {
    console.error('Error listing users:', error);
    throw new AdminListError(codeOf(error), 'No se ha podido leer la lista de usuarios.');
  }
}

// -----------------------------------------------------------------------------
// Update User Role
// -----------------------------------------------------------------------------

export async function updateUserRole(uid: string, newRole: UserRole): Promise<void> {
  if (!isConfigured || !db) return;

  const userRef = doc(db, COLLECTIONS.USERS, uid);
  await updateDoc(userRef, { role: newRole });
}
